import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import PinDialog from "@/components/PinDialog";
import { Search, Plus, ExternalLink, Loader2, Landmark, Globe } from "lucide-react";

const SOURCES = {
  archive: { label: "Archive Nepal", icon: Landmark, blurb: "Historic photographs from the Archive Nepal collection." },
  wiki: { label: "Wikimedia Commons", icon: Globe, blurb: "Freely-licensed images from Wikimedia Commons (please keep the credit)." },
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [source, setSource] = useState(params.get("source") === "wiki" ? "wiki" : "archive");
  const [sort, setSort] = useState("auto");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState({ current: 1, total_pages: 0, total_results: 0 });
  const [loading, setLoading] = useState(false);
  const [pinImage, setPinImage] = useState(null);

  const runSearch = useCallback(async (query, src, pageNum, sortBy, append = false) => {
    setLoading(true);
    try {
      const res = await api.get("/search", { params: { q: query, page: pageNum, size: 24, source: src, sort: sortBy } });
      setResults((prev) => (append ? [...prev, ...res.data.results] : res.data.results));
      setPage(res.data.page || {});
    } catch (e) {
      toast.error("Search failed, please try again");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initial = params.get("source") === "wiki" ? "wiki" : "archive";
    runSearch(params.get("q") || "", initial, 1, "auto", false);
  }, []); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    setParams(q ? { q } : {});
    runSearch(q, source, 1, sort, false);
  };

  const switchSource = (src) => {
    if (src === source) return;
    setSource(src);
    setResults([]);
    const next = {};
    if (q) next.q = q;
    if (src === "wiki") next.source = "wiki";
    setParams(next);
    runSearch(q, src, 1, sort, false);
  };

  const changeSort = (value) => {
    setSort(value);
    runSearch(q, source, 1, value, false);
  };

  const active = SOURCES[source];

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-serif font-bold text-ink">Search for Images</h1>
        <p className="text-muted-foreground mt-1">{active.blurb} Pin any image to a collection.</p>

        {/* Source toggle */}
        <div data-testid="source-toggle" className="mt-5 inline-flex items-center gap-1 bg-white border border-stoneline rounded-full p-1">
          {Object.entries(SOURCES).map(([key, s]) => {
            const Icon = s.icon;
            const activeTab = source === key;
            return (
              <button
                key={key}
                data-testid={`source-${key}`}
                onClick={() => switchSource(key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  activeTab ? "bg-brick text-parchment" : "text-ink/70 hover:text-brick"
                }`}
              >
                <Icon className="h-4 w-4" /> {s.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="mt-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[260px] max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${active.label}…`}
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-stoneline text-ink outline-none focus:ring-2 focus:ring-brick/40"
            />
          </div>
          <button data-testid="search-submit-btn" type="submit" className="bg-brick hover:bg-brick-dark text-parchment px-6 py-3 rounded-full font-semibold transition-colors">
            Search
          </button>
          {source === "archive" && (
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => changeSort(e.target.value)}
              className="py-3 px-4 rounded-full bg-white border border-stoneline text-ink text-sm font-medium outline-none focus:ring-2 focus:ring-brick/40 cursor-pointer"
            >
              <option value="auto">Sort: Default</option>
              <option value="newest">Newest additions</option>
              <option value="oldest">Oldest first</option>
              <option value="relevance">Most relevant</option>
            </select>
          )}
        </form>

        {page.total_results > 0 && (
          <p className="text-sm text-muted-foreground mt-4">{page.total_results >= 10000 ? "10,000+" : page.total_results.toLocaleString()} images found</p>
        )}

        {loading && results.length === 0 ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brick" /></div>
        ) : results.length === 0 ? (
          <div className="text-center py-24 text-muted-foreground">No results. Try a different search.</div>
        ) : (
          <div className="masonry columns-1 sm:columns-2 md:columns-3 lg:columns-4 mt-6">
            {results.map((item, i) => (
              <figure
                key={`${item.archive_id}-${i}`}
                data-testid="search-result-card"
                className="group relative rounded-lg overflow-hidden border border-stoneline bg-stone-100 shadow-sm hover:shadow-xl transition-all min-h-[180px]"
              >
                <img
                  src={item.image_url}
                  alt={item.title}
                  loading="lazy"
                  onError={(e) => { const f = e.target.closest("figure"); if (f) f.style.display = "none"; }}
                  className="w-full object-cover bg-stone-100"
                />
                {source === "wiki" && (
                  <span className="absolute top-2 left-2 bg-ink/70 text-parchment text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Globe className="h-2.5 w-2.5" /> Commons
                  </span>
                )}
                <figcaption className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                  <p className="text-parchment text-sm font-medium line-clamp-2">{item.title || "Untitled"}</p>
                  <p className="text-parchment/70 text-xs mt-0.5 line-clamp-1">{item.date || item.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      data-testid="pin-to-collection-btn"
                      onClick={() => setPinImage(item)}
                      className="inline-flex items-center gap-1.5 bg-brick hover:bg-brick-dark text-parchment text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add
                    </button>
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noreferrer" className="bg-parchment/90 text-ink p-1.5 rounded-full hover:bg-parchment">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        {results.length > 0 && page.current < page.total_pages && (
          <div className="flex justify-center mt-10">
            <button
              data-testid="load-more-btn"
              onClick={() => runSearch(q, source, page.current + 1, sort, true)}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-brick text-brick hover:bg-brick hover:text-parchment px-6 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Load more
            </button>
          </div>
        )}
      </div>

      <PinDialog open={!!pinImage} onOpenChange={(v) => !v && setPinImage(null)} image={pinImage} />
    </div>
  );
}
